<template>
  <div class="p-6 h-screen overflow-y-auto">
    <h1 class="text-2xl font-bold text-gray-800 mb-6">Role & Permission Comparison Tool</h1>

    <!-- Tab Navigation -->
    <div class="border-b border-gray-200 mb-6">
      <nav class="-mb-px flex space-x-8">
        <button
          @click="setActiveTab('tables')"
          :class="[
            'py-2 px-1 border-b-2 font-medium text-sm',
            activeTab === 'tables'
              ? 'border-indigo-500 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          ]"
        >
          Table Comparison
        </button>
        <button
          @click="setActiveTab('users')"
          :class="[
            'py-2 px-1 border-b-2 font-medium text-sm',
            activeTab === 'users'
              ? 'border-indigo-500 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          ]"
        >
          User Comparison
        </button>
      </nav>
    </div>

    <!-- Table Comparison Tab -->
    <div v-if="activeTab === 'tables'">
      <!-- Error State -->
      <div v-if="compareError" class="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        {{ compareError }}
      </div>

      <!-- Loading State -->
      <div v-if="loadingCompare" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>

      <!-- Comparison Data -->
      <div v-else-if="compareData">
        <!-- Summary Cards -->
        <div class="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div class="bg-white p-4 rounded-lg border border-gray-200">
            <div class="text-sm text-gray-500">Current Roles</div>
            <div class="text-2xl font-bold text-gray-900">{{ compareData.summary.current_role_count }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg border border-gray-200">
            <div class="text-sm text-gray-500">New Roles</div>
            <div class="text-2xl font-bold text-gray-900">{{ compareData.summary.new_role_count }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg border border-gray-200">
            <div class="text-sm text-gray-500">Role Mappings</div>
            <div class="text-2xl font-bold text-indigo-600">{{ compareData.summary.mapping_count || 0 }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg border border-gray-200">
            <div class="text-sm text-gray-500">Roles Added</div>
            <div class="text-2xl font-bold text-green-600">{{ compareData.summary.roles_added.length }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg border border-gray-200">
            <div class="text-sm text-gray-500">Roles Removed</div>
            <div class="text-2xl font-bold text-red-600">{{ compareData.summary.roles_removed.length }}</div>
          </div>
        </div>

        <!-- Role Mappings Table -->
        <div v-if="compareData.role_mappings && compareData.role_mappings.length > 0" class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3">Role Mappings (Old → New)</h2>
          <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div class="overflow-x-auto max-h-64 overflow-y-auto">
              <table class="w-full">
                <thead class="bg-gray-50 sticky top-0">
                  <tr>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Role</th>
                    <th class="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Role</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr v-for="mapping in compareData.role_mappings" :key="mapping.old_role_id" class="bg-white hover:bg-gray-50">
                    <td class="px-4 py-2 text-sm text-gray-900">{{ mapping.old_role_name }}</td>
                    <td class="px-4 py-2 text-center text-gray-400">→</td>
                    <td class="px-4 py-2 text-sm text-gray-900">{{ mapping.new_role_name }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Role Selection -->
        <div class="flex items-end gap-4 mb-6">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">Select Role to Compare</label>
            <Dropdown
              v-model="selectedRoleName"
              :options="roleOptions"
              option-label="label"
              option-value="value"
              placeholder="-- Select a role --"
              class="w-full"
              @change="onRoleSelected"
            />
          </div>
          <!-- <button
            @click="goToCsvUpload"
            class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            Upload CSV
          </button> -->
        </div>

        <!-- Changes Summary -->
        <div v-if="compareData.summary.roles_added.length > 0 || compareData.summary.roles_removed.length > 0" class="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div v-if="compareData.summary.roles_added.length > 0" class="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 class="text-sm font-semibold text-green-800 mb-2">New Roles Being Added</h3>
            <ul class="list-disc list-inside text-sm text-green-700">
              <li v-for="role in compareData.summary.roles_added" :key="role">{{ role }}</li>
            </ul>
          </div>
          <div v-if="compareData.summary.roles_removed.length > 0" class="bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 class="text-sm font-semibold text-red-800 mb-2">Roles Being Removed</h3>
            <ul class="list-disc list-inside text-sm text-red-700">
              <li v-for="role in compareData.summary.roles_removed" :key="role">{{ role }}</li>
            </ul>
          </div>
        </div>

        <!-- Permission Comparison for Selected Role or Mapping -->
        <div v-if="selectedRoleName || selectedMapping" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div class="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-800">
              <template v-if="selectedMapping">
                Permission Comparison: {{ selectedMapping.old_role_name }} → {{ selectedMapping.new_role_name }}
              </template>
              <template v-else>
                Permission Comparison: {{ selectedRoleName }}
              </template>
            </h3>
            <div class="mt-1 flex items-center gap-4 text-sm text-gray-600">
              <template v-if="selectedMapping">
                <span>Current ({{ selectedMapping.old_role_name }}): {{ selectedCurrentRole?.permission_count || 0 }} permissions</span>
                <span>New ({{ selectedMapping.new_role_name }}): {{ selectedNewRole?.permission_count || 0 }} permissions</span>
              </template>
              <template v-else>
                <span>Current: {{ selectedCurrentRole?.permission_count || 0 }} permissions</span>
                <span>New: {{ selectedNewRole?.permission_count || 0 }} permissions</span>
              </template>
            </div>
            <button
              v-if="selectedMapping"
              @click="clearMapping"
              class="mt-2 text-sm text-indigo-600 hover:text-indigo-800"
            >
              Clear mapping comparison
            </button>
          </div>

          <!-- Toggle for changes only -->
          <div class="px-4 py-2 border-b border-gray-200 flex items-center justify-between">
            <label class="inline-flex items-center cursor-pointer">
              <input type="checkbox" v-model="showChangesOnly" class="sr-only">
              <div class="relative">
                <div class="block bg-gray-300 w-10 h-6 rounded-full" :class="{'bg-indigo-600': showChangesOnly}"></div>
                <div class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform" :class="{'translate-x-4': showChangesOnly}"></div>
              </div>
              <span class="ml-3 text-sm text-gray-700">Show changes only</span>
            </label>
          </div>

          <div class="overflow-x-auto max-h-96 overflow-y-auto">
            <table class="w-full">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permission</th>
                  <th class="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Current</th>
                  <th class="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">New</th>
                  <th class="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr v-if="filteredPermissionComparison.length === 0" class="bg-white">
                  <td colspan="4" class="px-4 py-8 text-center text-gray-500 italic">
                    {{ showChangesOnly ? 'No permission changes for this role' : 'No permissions to display' }}
                  </td>
                </tr>
                <tr
                  v-for="perm in filteredPermissionComparison"
                  :key="perm.name"
                  class="bg-white hover:bg-gray-50"
                  :class="{
                    'bg-green-50': perm.status === 'added',
                    'bg-red-50': perm.status === 'removed'
                  }"
                >
                  <td class="px-4 py-2 text-sm text-gray-900">
                    {{ perm.display_name || perm.name }}
                  </td>
                  <td class="px-4 py-2 text-center">
                    <span v-if="perm.inCurrent" class="text-green-600">&#10003;</span>
                    <span v-else class="text-gray-300">-</span>
                  </td>
                  <td class="px-4 py-2 text-center">
                    <span v-if="perm.inNew" class="text-green-600">&#10003;</span>
                    <span v-else class="text-gray-300">-</span>
                  </td>
                  <td class="px-4 py-2 text-center text-sm">
                    <span v-if="perm.status === 'added'" class="text-green-600 font-medium">Added</span>
                    <span v-else-if="perm.status === 'removed'" class="text-red-600 font-medium">Removed</span>
                    <span v-else class="text-gray-400">Unchanged</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Legend -->
          <div class="px-4 py-2 bg-gray-50 border-t border-gray-200">
            <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
              <div class="flex items-center">
                <span class="bg-green-50 px-1.5 py-0.5 rounded mr-1.5">Added</span>
                Permission being added
              </div>
              <div class="flex items-center">
                <span class="bg-red-50 px-1.5 py-0.5 rounded mr-1.5">Removed</span>
                Permission being removed
              </div>
              <div class="flex items-center">
                <span class="text-gray-400 mr-1.5">Unchanged</span>
                No change
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- User Comparison Tab (existing functionality) -->
    <div v-if="activeTab === 'users'">
      <!-- Error State -->
      <div v-if="usersError" class="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        {{ usersError }}
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>

      <div v-else class="flex items-end gap-4 max-w-4xl">
        <div class="w-48">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Filter Users
          </label>
          <Dropdown
            v-model="userStatusFilter"
            :options="userStatusFilterOptions"
            option-label="label"
            option-value="value"
            class="w-full"
            @change="onUserStatusFilterChanged"
          />
        </div>
        <div class="flex-1">
          <label for="user-select" class="block text-sm font-medium text-gray-700 mb-2">
            Select User ({{ filteredUsers.length }} users)
          </label>
          <Dropdown
            v-model="selectedUser"
            :options="userOptions"
            option-label="label"
            option-value="value"
            placeholder="-- Select a user --"
            class="w-full"
            :disabled="filteredUsers.length === 0"
            @change="onUserSelected"
          />
          <p v-if="filteredUsers.length === 0" class="mt-2 text-sm text-red-600">
            No users found matching filter.
          </p>
        </div>
        <button
          @click="goToCsvUpload"
          class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
        >
          Upload CSVs
        </button>
      </div>

      <!-- Roles Table -->
      <div v-if="selectedUser && selectedUserData" class="mt-8">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">User Roles:</h2>

        <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">
                  Current Role(s)
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">
                  New Role(s)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="bg-white">
                <td class="px-6 py-4 whitespace-nowrap align-top">
                  <ul v-if="currentRoles.length > 0" class="list-disc list-inside text-sm text-gray-900">
                    <li v-for="role in currentRoles" :key="role.id">
                      {{ role.display_name || role.name }}
                    </li>
                  </ul>
                  <span v-else class="text-sm text-gray-500 italic">No current roles assigned</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap align-top" :class="{'bg-red-100': newRoles.length === 0}">
                  <ul v-if="newRoles.length > 0" class="space-y-1 text-sm text-gray-900">
                    <li v-for="role in newRoles" :key="role.id" class="flex items-center justify-between group">
                      <span>{{ role.display_name || role.name }}</span>
                      <button
                        @click="confirmRemoveNewRole(role)"
                        class="ml-2 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove this role"
                      >
                        <IconX class="w-4 h-4" />
                      </button>
                    </li>
                  </ul>
                  <span v-else class="text-sm text-red-600 font-medium">No new roles assigned</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Add New Role Dropdown -->
        <div class="mt-4 p-4 bg-gray-50 border-t border-gray-200">
          <label class="block text-sm font-medium text-gray-700 mb-2">Add a new role</label>
          <div class="flex items-center gap-3">
            <Dropdown
              v-model="selectedNewRoleToAdd"
              :options="availableNewRolesToAdd"
              option-label="label"
              option-value="value"
              placeholder="-- Select a role to add --"
              class="flex-1"
              :disabled="availableNewRolesToAdd.length === 0"
            />
            <button
              @click="confirmAddNewRole"
              :disabled="!selectedNewRoleToAdd"
              class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Role
            </button>
          </div>
          <p v-if="availableNewRolesToAdd.length === 0" class="mt-2 text-sm text-gray-500 italic">
            All available roles have been assigned to this user.
          </p>
        </div>
      </div>

      <!-- Permission Comparison Table -->
      <div v-if="selectedUser && selectedUserData" class="mt-8">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-gray-800">Permission Comparison:</h2>
          <div class="flex items-center gap-4">
            <span class="text-sm font-medium text-green-600">+{{ addedPermissionsCount }} added</span>
            <span class="text-sm font-medium text-red-600">-{{ removedPermissionsCount }} revoked</span>
            <label class="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                v-model="showUserChangesOnly"
                class="sr-only"
              >
              <div class="relative">
                <div class="block bg-gray-300 w-10 h-6 rounded-full" :class="{'bg-indigo-600': showUserChangesOnly}"></div>
                <div class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform" :class="{'translate-x-4': showUserChangesOnly}"></div>
              </div>
              <div class="ml-3 text-sm text-gray-700">Show changes only</div>
            </label>
          </div>
        </div>

        <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div class="overflow-x-auto max-h-96 overflow-y-auto">
            <table class="w-full">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permission
                  </th>
                  <th class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Create
                  </th>
                  <th class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Read
                  </th>
                  <th class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Update
                  </th>
                  <th class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delete
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <!-- No changes message when filtering -->
                <tr v-if="showUserChangesOnly && filteredPermissions.length === 0" class="bg-white">
                  <td colspan="5" class="px-4 py-8 text-center text-gray-500 italic">
                    No permission changes for this user
                  </td>
                </tr>

                <!-- Permission rows -->
                <tr v-for="group in filteredPermissions" :key="group.resource" class="bg-white hover:bg-gray-50">
                  <td class="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    {{ group.resource }}
                  </td>

                  <!-- Create column -->
                  <td class="px-3 py-2 whitespace-nowrap text-center"
                      :class="{
                        'bg-gray-100': group.create?.status === 'unchanged',
                        'bg-green-100': group.create?.status === 'added',
                        'bg-red-100': group.create?.status === 'removed'
                      }">
                    <span v-if="group.create?.status === 'unchanged'" class="text-black" title="Permission unchanged">&#10003;</span>
                    <span v-else-if="group.create?.status === 'added'" class="text-black" title="Permission being added">&#10003;</span>
                    <span v-else-if="group.create?.status === 'removed'" class="text-black" title="Permission being removed">&#10007;</span>
                    <span v-else class="text-gray-400">-</span>
                  </td>

                  <!-- Read column -->
                  <td class="px-3 py-2 whitespace-nowrap text-center"
                      :class="{
                        'bg-gray-100': group.read?.status === 'unchanged',
                        'bg-green-100': group.read?.status === 'added',
                        'bg-red-100': group.read?.status === 'removed'
                      }">
                    <span v-if="group.read?.status === 'unchanged'" class="text-black" title="Permission unchanged">&#10003;</span>
                    <span v-else-if="group.read?.status === 'added'" class="text-black" title="Permission being added">&#10003;</span>
                    <span v-else-if="group.read?.status === 'removed'" class="text-black" title="Permission being removed">&#10007;</span>
                    <span v-else class="text-gray-400">-</span>
                  </td>

                  <!-- Update column -->
                  <td class="px-3 py-2 whitespace-nowrap text-center"
                      :class="{
                        'bg-gray-100': group.update?.status === 'unchanged',
                        'bg-green-100': group.update?.status === 'added',
                        'bg-red-100': group.update?.status === 'removed'
                      }">
                    <span v-if="group.update?.status === 'unchanged'" class="text-black" title="Permission unchanged">&#10003;</span>
                    <span v-else-if="group.update?.status === 'added'" class="text-black" title="Permission being added">&#10003;</span>
                    <span v-else-if="group.update?.status === 'removed'" class="text-black" title="Permission being removed">&#10007;</span>
                    <span v-else class="text-gray-400">-</span>
                  </td>

                  <!-- Delete column -->
                  <td class="px-3 py-2 whitespace-nowrap text-center"
                      :class="{
                        'bg-gray-100': group.delete?.status === 'unchanged',
                        'bg-green-100': group.delete?.status === 'added',
                        'bg-red-100': group.delete?.status === 'removed'
                      }">
                    <span v-if="group.delete?.status === 'unchanged'" class="text-black" title="Permission unchanged">&#10003;</span>
                    <span v-else-if="group.delete?.status === 'added'" class="text-black" title="Permission being added">&#10003;</span>
                    <span v-else-if="group.delete?.status === 'removed'" class="text-black" title="Permission being removed">&#10007;</span>
                    <span v-else class="text-gray-400">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Legend -->
          <div class="px-4 py-2 bg-gray-50 border-t border-gray-200">
            <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
              <div class="flex items-center">
                <span class="bg-gray-100 px-1.5 py-0.5 rounded text-black mr-1.5">&#10003;</span>
                Unchanged
              </div>
              <div class="flex items-center">
                <span class="bg-green-100 px-1.5 py-0.5 rounded text-black mr-1.5">&#10003;</span>
                Being added
              </div>
              <div class="flex items-center">
                <span class="bg-red-100 px-1.5 py-0.5 rounded text-black mr-1.5">&#10007;</span>
                Being removed
              </div>
              <div class="flex items-center">
                <span class="text-gray-400 mr-1.5">-</span>
                No permission
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sticky Action Bar -->
      <div v-if="selectedUser && selectedUserData" class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div class="max-w-7xl mx-auto px-6 py-4">
          <div class="flex justify-between items-center">
            <button
              @click="goToNextUser"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              Skip / Next User
            </button>

            <div class="flex items-center space-x-3">
              <span class="text-sm text-gray-600">
                Review changes for {{ selectedUserData?.name }}
              </span>
              <button
                @click="rejectRoleChanges"
                class="px-6 py-2 text-sm font-medium text-white bg-red-500 border border-transparent rounded-md shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                Reject Role Changes
              </button>
              <button
                @click="acceptRoleChanges"
                class="px-6 py-2 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-md shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                Accept Role Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom padding to prevent content overlap with sticky bar -->
      <div v-if="selectedUser && selectedUserData" class="h-20"></div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { IconX } from '@tabler/icons-vue'

export default {
  name: "NewRolesView",
  components: {
    IconX,
  },
  data() {
    return {
      activeTab: 'tables',
      // Table comparison data
      compareData: null,
      compareError: null,
      loadingCompare: false,
      selectedRoleName: null,
      selectedMapping: null,
      showChangesOnly: false,
      // User comparison data
      selectedUser: null,
      users: [],
      allPermissions: [],
      loading: false,
      selectedUserData: null,
      currentRoles: [],
      newRoles: [],
      currentPermissions: [],
      newPermissions: [],
      loadingRoles: false,
      showUserChangesOnly: false,
      usersError: null,
      userStatusFilter: 'all',
      selectedNewRoleToAdd: null,
      allNewRoles: [],
    }
  },
  computed: {
    roleOptions() {
      if (!this.compareData) return []

      const options = [{ label: '-- Select a role --', value: null }]

      // Add current roles with mapping info
      const currentRoleOptions = this.compareData.current_roles.map(r => {
        const mapsTo = r.maps_to ? ` → ${r.maps_to}` : ' (no mapping)'
        return {
          label: `[Current] ${r.name}${mapsTo}`,
          value: r.name
        }
      })

      // Add new roles with mapping info
      const newRoleOptions = this.compareData.new_roles.map(r => {
        const mapsFrom = r.maps_from && r.maps_from.length > 0
          ? ` ← ${r.maps_from.join(', ')}`
          : ' (new role)'
        return {
          label: `[New] ${r.name}${mapsFrom}`,
          value: r.name
        }
      })

      return [
        ...options,
        { label: '--- Current Roles ---', value: null, disabled: true },
        ...currentRoleOptions,
        { label: '--- New Roles ---', value: null, disabled: true },
        ...newRoleOptions
      ]
    },

    selectedCurrentRole() {
      if (!this.compareData) return null
      // If mapping is selected, use the old role from the mapping
      if (this.selectedMapping) {
        return this.compareData.current_roles.find(r => r.name === this.selectedMapping.old_role_name)
      }
      if (!this.selectedRoleName) return null
      return this.compareData.current_roles.find(r => r.name === this.selectedRoleName)
    },

    selectedNewRole() {
      if (!this.compareData) return null
      // If mapping is selected, use the new role from the mapping
      if (this.selectedMapping) {
        return this.compareData.new_roles.find(r => r.name === this.selectedMapping.new_role_name)
      }
      if (!this.selectedRoleName) return null
      return this.compareData.new_roles.find(r => r.name === this.selectedRoleName)
    },

    permissionComparison() {
      if (!this.compareData) return []
      if (!this.selectedRoleName && !this.selectedMapping) return []

      const currentPerms = new Set(
        (this.selectedCurrentRole?.permissions || []).map(p => p.name)
      )
      const newPerms = new Set(
        (this.selectedNewRole?.permissions || []).map(p => p.name)
      )

      // Get all permissions that appear in either role
      const allPerms = new Map()

      this.compareData.all_permissions.forEach(p => {
        const inCurrent = currentPerms.has(p.name)
        const inNew = newPerms.has(p.name)

        if (inCurrent || inNew) {
          let status = 'unchanged'
          if (inCurrent && !inNew) status = 'removed'
          else if (!inCurrent && inNew) status = 'added'

          allPerms.set(p.name, {
            ...p,
            inCurrent,
            inNew,
            status
          })
        }
      })

      return Array.from(allPerms.values()).sort((a, b) => a.name.localeCompare(b.name))
    },

    filteredPermissionComparison() {
      if (!this.showChangesOnly) return this.permissionComparison
      return this.permissionComparison.filter(p => p.status !== 'unchanged')
    },

    userStatusFilterOptions() {
      return [
        { label: 'All Users', value: 'all' },
        { label: 'Pending', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' }
      ]
    },

    filteredUsers() {
      if (this.userStatusFilter === 'all') {
        return this.users
      }
      return this.users.filter(user => user.user_migration_status === this.userStatusFilter)
    },

    userOptions() {
      const options = [
        { label: '-- Select a user --', value: null }
      ]

      return options.concat(
        this.filteredUsers.map(user => ({
          label: `${user.name} (${user.email})`,
          value: user.id
        }))
      )
    },

    groupedPermissions() {
      // Group all permissions by resource (the part after the colon)
      const currentPermIds = new Set(this.currentPermissions.map(p => p.id))
      const newPermIds = new Set(this.newPermissions.map(p => p.id))
      const groups = {}

      this.allPermissions.forEach(permission => {
        const parts = permission.name.split(':')
        if (parts.length === 2) {
          const [action, resource] = parts

          if (!groups[resource]) {
            groups[resource] = {
              resource: resource,
              create: null,
              read: null,
              update: null,
              delete: null
            }
          }

          // Calculate status for this permission
          const inCurrent = currentPermIds.has(permission.id)
          const inNew = newPermIds.has(permission.id)

          let status = 'none'
          if (inCurrent && inNew) status = 'unchanged'
          else if (inCurrent && !inNew) status = 'removed'
          else if (!inCurrent && inNew) status = 'added'

          const permData = {
            ...permission,
            inCurrent,
            inNew,
            status
          }

          // Map the permission to the appropriate CRUD action
          if (action === 'create' || action === 'update' || action === 'read' || action === 'delete') {
            groups[resource][action] = permData
          }
        }
      })

      // Convert to array and sort by resource name
      return Object.values(groups).sort((a, b) => a.resource.localeCompare(b.resource))
    },

    filteredPermissions() {
      if (!this.showUserChangesOnly) {
        return this.groupedPermissions
      }

      // Filter to show only permission groups that have at least one change
      return this.groupedPermissions.filter(group => {
        return (group.create?.status === 'added' || group.create?.status === 'removed') ||
               (group.read?.status === 'added' || group.read?.status === 'removed') ||
               (group.update?.status === 'added' || group.update?.status === 'removed') ||
               (group.delete?.status === 'added' || group.delete?.status === 'removed')
      })
    },

    addedPermissionsCount() {
      let count = 0
      this.groupedPermissions.forEach(group => {
        if (group.create?.status === 'added') count++
        if (group.read?.status === 'added') count++
        if (group.update?.status === 'added') count++
        if (group.delete?.status === 'added') count++
      })
      return count
    },

    removedPermissionsCount() {
      let count = 0
      this.groupedPermissions.forEach(group => {
        if (group.create?.status === 'removed') count++
        if (group.read?.status === 'removed') count++
        if (group.update?.status === 'removed') count++
        if (group.delete?.status === 'removed') count++
      })
      return count
    },

    availableNewRolesToAdd() {
      if (!this.allNewRoles || this.allNewRoles.length === 0) return []

      // Filter out roles that are already assigned to the user
      const assignedRoleIds = new Set(this.newRoles.map(r => r.id))

      return this.allNewRoles
        .filter(role => !assignedRoleIds.has(role.id))
        .map(role => ({
          label: role.display_name || role.name,
          value: role.id,
          role: role
        }))
    }
  },
  watch: {
    showChangesOnly(value) {
      this.updateChangesOnlyQueryParam(value)
    },
    showUserChangesOnly(value) {
      this.updateChangesOnlyQueryParam(value)
    }
  },
  async mounted() {
    // Read tab from URL query parameter
    const tab = this.$route.query.tab
    if (tab === 'tables' || tab === 'users') {
      this.activeTab = tab
    }

    // Read user_status filter from URL query parameter
    const statusFilter = this.$route.query.status
    if (['all', 'pending', 'approved', 'rejected'].includes(statusFilter)) {
      this.userStatusFilter = statusFilter
    }

    // Fetch both in parallel so one doesn't block the other
    await Promise.all([
      this.fetchCompareData(),
      this.fetchUsers()
    ])

    // Read user_id from URL query parameter after users are loaded
    const userId = this.$route.query.user_id
    if (userId && this.users.length > 0) {
      const parsedId = parseInt(userId, 10)
      if (this.users.some(u => u.id === parsedId)) {
        this.selectedUser = parsedId
        await this.onUserSelected({ value: parsedId })
      }
    }

    // Read changes_only from URL query parameter
    if (this.$route.query.changes_only === 'true') {
      this.showChangesOnly = true
      this.showUserChangesOnly = true
    }
  },
  methods: {
    async fetchCompareData() {
      try {
        this.loadingCompare = true
        this.compareError = null
        const response = await axios.get('role-migration-csv/compare')
        this.compareData = response.data
        // Store all new roles for the add dropdown
        this.allNewRoles = response.data.new_roles || []
      } catch (error) {
        console.error('Failed to fetch comparison data:', error)
        this.compareError = error.response?.data?.error || 'Failed to load comparison data. Make sure migration tables are set up.'
      } finally {
        this.loadingCompare = false
      }
    },

    onRoleSelected(event) {
      this.selectedRoleName = event.value
      this.selectedMapping = null // Clear mapping when selecting a role directly
    },

    compareMapping(mapping) {
      this.selectedMapping = mapping
      this.selectedRoleName = null // Clear direct role selection
    },

    clearMapping() {
      this.selectedMapping = null
    },

    async fetchUsers() {
      try {
        this.loading = true
        this.usersError = null
        const response = await axios.get('users/all')

        // Handle new response structure
        if (response.data.users && response.data.permissions) {
          this.users = response.data.users
          this.allPermissions = response.data.permissions
        } else if (response.data.data) {
          // Fallback to paginated structure
          this.users = response.data.data
        } else if (Array.isArray(response.data)) {
          // Fallback to array structure
          this.users = response.data
        } else {
          this.users = []
        }

        console.log('Loaded users:', this.users.length)
      } catch (error) {
        console.error('Failed to fetch users:', error)
        this.usersError = error.response?.data?.message || 'Failed to load users'
      } finally {
        this.loading = false
      }
    },

    async onUserSelected(event) {
      const userId = event.value

      if (!userId) {
        this.selectedUserData = null
        this.currentRoles = []
        this.newRoles = []
        this.currentPermissions = []
        this.newPermissions = []
        // Remove user_id from URL
        const query = { ...this.$route.query }
        delete query.user_id
        this.$router.replace({ query })
        return
      }

      // Update URL with selected user_id
      this.$router.replace({ query: { ...this.$route.query, user_id: userId } })

      // Find the selected user data
      this.selectedUserData = this.users.find(user => user.id === userId)

      // Extract new roles from user data (already loaded via users/all)
      this.newRoles = this.selectedUserData?.new_roles || []

      // Extract permissions from new roles
      this.extractNewPermissions()

      // Fetch user's current roles
      await this.fetchUserRoles(userId)
    },

    async fetchUserRoles(userId) {
      try {
        this.loadingRoles = true
        const response = await axios.get(`users/${userId}?include=roles.permissions`)
        const userData = response.data

        // Extract roles from the user data
        this.currentRoles = userData.roles || []

        // Extract permissions from the loaded roles
        this.extractCurrentPermissionsFromRoles()
      } catch (error) {
        console.error('Failed to fetch user roles:', error)
        this.currentRoles = []
        this.currentPermissions = []
      } finally {
        this.loadingRoles = false
      }
    },

    extractCurrentPermissionsFromRoles() {
      // Extract permissions from the loaded roles
      const permissions = []

      this.currentRoles.forEach(role => {
        if (role.permissions && Array.isArray(role.permissions)) {
          role.permissions.forEach(permission => {
            // Avoid duplicates
            if (!permissions.find(p => p.id === permission.id)) {
              permissions.push(permission)
            }
          })
        }
      })

      this.currentPermissions = permissions
    },

    extractNewPermissions() {
      // Extract permissions from the loaded new roles
      const permissions = []

      this.newRoles.forEach(role => {
        if (role.permissions && Array.isArray(role.permissions)) {
          role.permissions.forEach(permission => {
            // Avoid duplicates
            if (!permissions.find(p => p.id === permission.id)) {
              permissions.push(permission)
            }
          })
        }
      })

      this.newPermissions = permissions
    },

    hasCurrentPermission(permission) {
      if (!permission) return false
      return this.currentPermissions.some(p => p.id === permission.id)
    },

    hasNewPermission(permission) {
      if (!permission) return false
      return this.newPermissions.some(p => p.id === permission.id)
    },

    getPermissionStatus(permission) {
      if (!permission) return 'none'

      const hasCurrent = this.hasCurrentPermission(permission)
      const hasNew = this.hasNewPermission(permission)

      if (hasCurrent && hasNew) {
        return 'unchanged' // Gray tick - exists in both
      } else if (hasCurrent && !hasNew) {
        return 'removed' // Red cross - being removed
      } else if (!hasCurrent && hasNew) {
        return 'added' // Green tick - being added
      } else {
        return 'none' // No permission in either
      }
    },

    goToNextUser() {
      // Find the current user index in the filtered list
      const currentIndex = this.filteredUsers.findIndex(user => user.id === this.selectedUser)

      // If we found the current user and there's a next user
      if (currentIndex !== -1 && currentIndex < this.filteredUsers.length - 1) {
        const nextUser = this.filteredUsers[currentIndex + 1]
        this.selectedUser = nextUser.id
        this.onUserSelected({ value: nextUser.id })
      } else if (this.filteredUsers.length > 0) {
        // If we're at the end, go back to the first user
        const firstUser = this.filteredUsers[0]
        this.selectedUser = firstUser.id
        this.onUserSelected({ value: firstUser.id })
      }
    },

    async acceptRoleChanges() {
      if (!this.selectedUserData) return

      try {
        const userName = this.selectedUserData.name
        const newRoleNames = this.newRoles.map(role => role.display_name || role.name).join(', ')

        const confirmed = confirm(
          `Are you sure you want to accept the role changes for ${userName}?\n\n` +
          `New roles: ${newRoleNames || 'None'}`
        )

        if (confirmed) {
          // Remember the next user before updating status
          const nextUserId = this.getNextFilteredUserId()
          await this.updateMigrationStatus(this.selectedUserData.id, 'approved')
          this.selectUser(nextUserId)
        }
      } catch (error) {
        console.error('Error accepting role changes:', error)
        alert('Failed to accept role changes. Please try again.')
      }
    },

    async rejectRoleChanges() {
      if (!this.selectedUserData) return

      try {
        const userName = this.selectedUserData.name

        const confirmed = confirm(
          `Are you sure you want to reject the role changes for ${userName}?`
        )

        if (confirmed) {
          // Remember the next user before updating status
          const nextUserId = this.getNextFilteredUserId()
          await this.updateMigrationStatus(this.selectedUserData.id, 'rejected')
          this.selectUser(nextUserId)
        }
      } catch (error) {
        console.error('Error rejecting role changes:', error)
        alert('Failed to reject role changes. Please try again.')
      }
    },

    async updateMigrationStatus(userId, status) {
      await axios.patch(`users/${userId}/migration-status`, { status })

      // Update the user's status in our local list
      const user = this.users.find(u => u.id === userId)
      if (user) {
        user.user_migration_status = status
      }
    },

    getNextFilteredUserId() {
      const currentIndex = this.filteredUsers.findIndex(user => user.id === this.selectedUser)

      // If current user not found or at the end, return null
      if (currentIndex === -1) return null

      // Get the next user in the list
      const nextIndex = currentIndex + 1
      if (nextIndex < this.filteredUsers.length) {
        return this.filteredUsers[nextIndex].id
      }

      // If we're at the end, wrap to the first user (if it's not the current user)
      if (this.filteredUsers.length > 1) {
        return this.filteredUsers[0].id
      }

      return null
    },

    selectUser(userId) {
      if (!userId) {
        // No more users to select
        this.selectedUser = null
        this.selectedUserData = null
        this.currentRoles = []
        this.newRoles = []
        this.currentPermissions = []
        this.newPermissions = []
        // Remove user_id from URL
        const query = { ...this.$route.query }
        delete query.user_id
        this.$router.replace({ query })
        return
      }

      this.selectedUser = userId
      this.onUserSelected({ value: userId })
    },

    goToCsvUpload() {
      this.$router.push({ name: 'new-roles-csv-upload' })
    },

    setActiveTab(tab) {
      this.activeTab = tab
      this.$router.replace({ query: { ...this.$route.query, tab } })
    },

    updateChangesOnlyQueryParam(value) {
      const query = { ...this.$route.query }
      if (value) {
        query.changes_only = 'true'
      } else {
        delete query.changes_only
      }
      this.$router.replace({ query })
    },

    onUserStatusFilterChanged(event) {
      const status = event.value
      const query = { ...this.$route.query }
      if (status === 'all') {
        delete query.status
      } else {
        query.status = status
      }
      // Clear selected user if they're no longer in the filtered list
      if (this.selectedUser && !this.filteredUsers.some(u => u.id === this.selectedUser)) {
        this.selectedUser = null
        delete query.user_id
        this.selectedUserData = null
        this.currentRoles = []
        this.newRoles = []
        this.currentPermissions = []
        this.newPermissions = []
      }
      this.$router.replace({ query })
    },

    async confirmRemoveNewRole(role) {
      const roleName = role.display_name || role.name
      const userName = this.selectedUserData?.name || 'this user'

      const confirmed = confirm(
        `Are you sure you want to remove the role "${roleName}" from ${userName}?\n\n` +
        `This will remove the role from their pending migration roles.`
      )

      if (confirmed) {
        await this.removeNewRole(role)
      }
    },

    async removeNewRole(role) {
      if (!this.selectedUserData) return

      try {
        const response = await axios.delete(`users/${this.selectedUserData.id}/new-roles`, {
          data: { role_id: role.id }
        })

        // Update the local new roles from the response
        this.newRoles = response.data.user.new_roles || []
        this.selectedUserData.new_roles = this.newRoles

        // Also update in the users array
        const userInList = this.users.find(u => u.id === this.selectedUserData.id)
        if (userInList) {
          userInList.new_roles = this.newRoles
        }

        // Re-extract permissions from the updated roles
        this.extractNewPermissions()

      } catch (error) {
        console.error('Failed to remove role:', error)
        alert(error.response?.data?.message || 'Failed to remove role. Please try again.')
      }
    },

    async confirmAddNewRole() {
      if (!this.selectedNewRoleToAdd) return

      const roleOption = this.availableNewRolesToAdd.find(r => r.value === this.selectedNewRoleToAdd)
      const roleName = roleOption?.label || 'the selected role'
      const userName = this.selectedUserData?.name || 'this user'

      const confirmed = confirm(
        `Are you sure you want to add the role "${roleName}" to ${userName}?\n\n` +
        `This will add the role to their pending migration roles.`
      )

      if (confirmed) {
        await this.addNewRole(this.selectedNewRoleToAdd)
      }
    },

    async addNewRole(roleId) {
      if (!this.selectedUserData) return

      try {
        const response = await axios.post(`users/${this.selectedUserData.id}/new-roles`, {
          role_id: roleId
        })

        // Update the local new roles from the response
        this.newRoles = response.data.user.new_roles || []
        this.selectedUserData.new_roles = this.newRoles

        // Also update in the users array
        const userInList = this.users.find(u => u.id === this.selectedUserData.id)
        if (userInList) {
          userInList.new_roles = this.newRoles
        }

        // Re-extract permissions from the updated roles
        this.extractNewPermissions()

        // Clear the dropdown selection
        this.selectedNewRoleToAdd = null

      } catch (error) {
        console.error('Failed to add role:', error)
        alert(error.response?.data?.message || 'Failed to add role. Please try again.')
      }
    }
  }
}
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
